import { FundFrom, mARIOToken } from '@ar.io/sdk';
import { ARIOFundingSelector } from '@src/components/forms/ARIOFundingSelector/ARIOFundingSelector';
import { ArIOTokenIcon, TurboIcon } from '@src/components/icons';
import { Checkbox } from '@src/components/inputs/Checkbox';
import { SelectDropdown } from '@src/components/inputs/Select';
import TurboTopUpModal from '@src/components/modals/turbo/TurboTopUpModal';
import {
  useArIOLiquidBalance,
  useArIOStakedAndVaultedBalance,
} from '@src/hooks/useArIOBalance';
import { useTurboArNSClient } from '@src/hooks/useTurboArNSClient';
import { useTurboCreditBalance } from '@src/hooks/useTurboCreditBalance';
import { PaymentInformation } from '@src/services/turbo/TurboArNSClient';
import { useGlobalState, useWalletState } from '@src/state';
import { formatARIOWithCommas } from '@src/utils';
import { getBaseChainId } from '@src/utils/baseNetwork';
import {
  BASE_ARIO_CONTRACT,
  BASE_TOKEN_CONFIG,
  BASE_USDC_CONTRACT,
  BaseTokenType,
  CryptoPaymentToken,
  isBaseToken,
} from '@src/utils/constants';
import eventEmitter from '@src/utils/events';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementOptions } from '@stripe/stripe-js';
import Ar from 'arweave/node/ar';
import {
  Circle,
  CircleCheck,
  CircleXIcon,
  CreditCard,
  Info,
} from 'lucide-react';
import { Tabs } from 'radix-ui';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { isEmail } from 'validator';
// NOTE (de-AO refactor): wagmi hooks crash without a `WagmiProvider`, which
// the Solana-only refactor removed. Stub them out — the EVM-funded payment
// branches are unreachable from the Solana-only UI but the wagmi hook
// calls themselves still ran on every render and crashed the form.
const useAccount = () => ({ connector: undefined, address: undefined }) as any;
const useBalance = (_args?: unknown) => ({ data: undefined }) as any;

export type PaymentMethod = 'card' | 'crypto' | 'credits';
export type ARIOCryptoOptions = 'ARIO' | 'dARIO' | 'tARIO';
export type CryptoOptions = ARIOCryptoOptions | BaseTokenType;

/**
 * UI-only gates — checkout logic for card / Turbo / Base is kept for later.
 * Card is also hard-disabled below for Solana (Turbo settlement); flip flags
 * when those paths are ready.
 */
const DISABLE_CREDIT_CARD_CHECKOUT_UI = true;
const DISABLE_TURBO_CREDITS_CHECKOUT_UI = true;
const DISABLE_BASE_CRYPTO_CHECKOUT_UI = true;

const FormEntry: FC<{
  name: string;
  label: string;
  children: ReactNode;
  errorText?: string;
}> = ({ name, label, children, errorText }) => {
  return (
    <div className="flex flex-col gap-1">
      <label
        className="flex text-sm text-grey whitespace-nowrap"
        htmlFor={name}
      >
        {label}
      </label>
      <div className="flex w-full rounded border border-dark-grey">
        {children}
      </div>
      {errorText && <div className="flex text-xs text-error">{errorText}</div>}
    </div>
  );
};

function CardPanel({
  setIsValid,
  onPaymentInformationChange,
  promoCode,
  setPromoCode,
}: {
  promoCode?: string;
  setPromoCode: (promoCode?: string) => Promise<void>;
  setIsValid: (valid: boolean) => void;
  onPaymentInformationChange: (paymentInformation: PaymentInformation) => void;
}) {
  const [{ walletAddress }] = useWalletState();
  const turbo = useTurboArNSClient();
  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [localPromoCode, setLocalPromoCode] = useState<string>();
  const [cardComplete, setCardComplete] = useState<boolean>(false);

  const [keepMeUpdated, setKeepMeUpdated] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const [nameError, setNameError] = useState<string>();
  const [cardError, setCardError] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  const [promoCodeError, setPromoCodeError] = useState<string>();

  async function updatePaymentInformation() {
    const cardElement = elements?.getElement(CardElement);
    if (!cardElement || !stripe || !name || !cardComplete) return;

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name,
        email: keepMeUpdated ? email : undefined,
      },
    });
    if (error) {
      eventEmitter.emit('error', error);
    } else if (paymentMethod) {
      onPaymentInformationChange({
        paymentMethodId: paymentMethod.id,
        email,
      });
    }
  }

  useEffect(() => {
    updatePaymentInformation();
  }, [name, email, keepMeUpdated, termsAccepted, promoCode, cardComplete]);

  useEffect(() => {
    if (
      nameError ||
      cardError ||
      emailError ||
      promoCodeError ||
      !cardComplete ||
      !termsAccepted ||
      !name
    ) {
      setIsValid(false);
      return;
    }
    setIsValid(true);
  }, [
    nameError,
    cardError,
    emailError,
    promoCodeError,
    cardComplete,
    termsAccepted,
    keepMeUpdated,
    name,
  ]);

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: 'white',
        '::placeholder': {
          color: '#7d7d85',
        },
      },
    },
    hidePostalCode: true,
  };

  return (
    <div className="flex size-full flex-col items-start text-left text-white">
      <div className="flex w-full flex-col text-grey">
        <form className="flex  flex-col gap-3">
          <FormEntry name="name" label="Name on Card *" errorText={nameError}>
            <input
              className="size-full flex  bg-transparent px-4 py-2 outline-none text-white"
              type="text"
              name="name"
              value={name}
              onChange={(e) => {
                const v = e.target.value ?? '';
                const cleaned = v.replace(/[^a-zA-Z\s]/g, '');
                setName(cleaned);
                setNameError(
                  cleaned.length === 0 ? 'Name is required' : undefined,
                );
              }}
            ></input>
          </FormEntry>
          <FormEntry
            name="creditCard"
            label="Credit Card *"
            errorText={cardError}
          >
            <CardElement
              options={cardElementOptions}
              className="size-full bg-transparent px-4 p-2 outline-none whitespace-nowrap h-[2.188rem]"
              onChange={({ error, complete }) => {
                setCardError(error?.message);
                setCardComplete(complete);
              }}
            />
          </FormEntry>

          {promoCode ? (
            <div className="flex flex-col gap-1">
              <span className="flex text-sm text-grey whitespace-nowrap gap-2">
                Promo Code{' '}
                <span className="text-success font-bold">{promoCode}</span>
              </span>
              <div className="flex max-w-fit items-center gap-2 text-sm text-grey whitespace-nowrap">
                Promo code successfully applied.
                <button
                  className="text-white"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (walletAddress && turbo) {
                      try {
                        setPromoCode(undefined);
                        setPromoCodeError(undefined);
                      } catch (e: unknown) {
                        console.error(e);
                        setPromoCodeError(
                          'Error removing promo code, please try again.',
                        );
                      }
                    }
                  }}
                >
                  <CircleXIcon className="size-4 text-success" />
                </button>
              </div>
              {promoCodeError && (
                <div className="text-xs text-error">{promoCodeError}</div>
              )}
            </div>
          ) : (
            <FormEntry
              name="promoCode"
              label="Promo Code"
              errorText={promoCodeError}
            >
              <div className="relative flex w-full">
                <input
                  className="peer flex size-full bg-transparent px-4 py-2 outline-none text-white"
                  type="text"
                  name="promoCode"
                  value={localPromoCode || ''}
                  onChange={(e) => {
                    const v = e.target.value ?? '';
                    const cleaned = v.replace(/[^a-zA-Z0-9\s]/g, '');
                    setLocalPromoCode(cleaned);
                    setPromoCodeError(undefined);
                  }}
                ></input>
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-xs text-white opacity-0 transition-opacity focus:opacity-100 peer-focus:opacity-100"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (
                      walletAddress &&
                      localPromoCode &&
                      localPromoCode.length > 0 &&
                      turbo
                    ) {
                      try {
                        await setPromoCode(localPromoCode);
                      } catch (e: any) {
                        console.error(e);
                        setPromoCodeError(e.message + ' Invalid promo code.');
                      }
                    } else {
                      setPromoCode(undefined);
                      setPromoCodeError('Promo code is invalid or expired.');
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            </FormEntry>
          )}
          <div className="flex size-full flex-col gap-8">
            <FormEntry
              name="email"
              label="Enter email for a receipt"
              errorText={emailError}
            >
              <input
                type="text"
                className="flex size-full bg-transparent px-4 py-2 outline-none text-white"
                name="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  setEmailError(
                    newEmail.length === 0 || isEmail(e.target.value)
                      ? undefined
                      : 'Please enter a valid email address.',
                  );
                }}
              ></input>
            </FormEntry>
            <div className="flex text-xs flex-col items-start gap-3">
              <Checkbox
                className="text-xs"
                id="keepMeUpdatedCheckbox"
                disabled={!email}
                label="Keep me up to date on news and promotions."
                checked={keepMeUpdated}
                onCheckedChange={(checked) =>
                  setKeepMeUpdated(Boolean(checked.valueOf()))
                }
              />
              <Checkbox
                className="text-xs"
                id="acceptTermsCheckbox"
                label="I agree to the terms of service and the privacy policy."
                checked={termsAccepted}
                onCheckedChange={(checked) =>
                  setTermsAccepted(Boolean(checked.valueOf()))
                }
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentOptionsForm({
  fundingSource = 'balance',
  paymentMethod = 'crypto',
  onFundingSourceChange,
  onPaymentMethodChange,
  onPaymentInformationChange,
  onCryptoTokenChange,
  isInsufficientBalance,
  setIsValid,
  promoCode,
  setPromoCode,
}: {
  promoCode?: string;
  setPromoCode: (promoCode?: string) => Promise<void>;
  fundingSource?: FundFrom;
  paymentMethod?: PaymentMethod;
  onFundingSourceChange: (fundingSource: FundFrom) => void;
  onPaymentMethodChange: (paymentMethod: PaymentMethod) => void;
  onPaymentInformationChange: (paymentInformation: PaymentInformation) => void;
  onCryptoTokenChange?: (token: CryptoPaymentToken) => void;
  isInsufficientBalance: boolean;
  setIsValid: (valid: boolean) => void;
}) {
  const [{ arioTicker }] = useGlobalState();
  const [{ wallet }] = useWalletState();
  const formattedARIOTicker = `${arioTicker}` as CryptoOptions;

  // Check if connected with an Ethereum wallet
  const isEthereumWallet = wallet?.tokenType === 'ethereum';

  // Build crypto dropdown options - include Base tokens for ETH wallets
  const cryptoDropdownOptions = useMemo(() => {
    const options: { label: string; value: CryptoOptions }[] = [
      { label: `${formattedARIOTicker} (No fee)`, value: formattedARIOTicker },
    ];

    if (isEthereumWallet && !DISABLE_BASE_CRYPTO_CHECKOUT_UI) {
      options.push(
        {
          label: `${BASE_TOKEN_CONFIG['base-ario'].label} (No fee)`,
          value: 'base-ario' as const,
        },
        {
          label: BASE_TOKEN_CONFIG['base-eth'].label,
          value: 'base-eth' as const,
        },
        {
          label: BASE_TOKEN_CONFIG['base-usdc'].label,
          value: 'base-usdc' as const,
        },
      );
    }

    return options;
  }, [arioTicker, isEthereumWallet, formattedARIOTicker]);

  const { data: liquidBalance } = useArIOLiquidBalance();
  const { data: stakedAndVaultedBalance } = useArIOStakedAndVaultedBalance();

  const liquidArIOBalance = useMemo(() => {
    return liquidBalance ? new mARIOToken(liquidBalance).toARIO().valueOf() : 0;
  }, [liquidBalance]);
  const stakedAndVaultedArIOBalance = useMemo(() => {
    return stakedAndVaultedBalance
      ? new mARIOToken(stakedAndVaultedBalance.totalDelegatedStake)
          .toARIO()
          .valueOf() +
          new mARIOToken(stakedAndVaultedBalance.totalVaultedStake)
            .toARIO()
            .valueOf()
      : 0;
  }, [stakedAndVaultedBalance]);
  const allArIOBalance = useMemo(() => {
    return liquidArIOBalance + stakedAndVaultedArIOBalance;
  }, [liquidArIOBalance, stakedAndVaultedArIOBalance]);

  const { data: turboCreditBalanceRes } = useTurboCreditBalance();
  const turboCreditBalance = useMemo(() => {
    if (!turboCreditBalanceRes) return '0';
    const ar = new Ar();
    return formatARIOWithCommas(
      parseFloat(ar.winstonToAr(turboCreditBalanceRes.effectiveBalance)),
    );
  }, [turboCreditBalanceRes]);

  const [selectedCrypto, setSelectedCrypto] =
    useState<CryptoOptions>(formattedARIOTicker);

  // Check if a Base token is selected
  const selectedBaseToken = isBaseToken(selectedCrypto)
    ? (selectedCrypto as BaseTokenType)
    : undefined;

  // Get wagmi account for Base token balance
  const { address: ethAddress } = useAccount();

  // Fetch ETH balance on Base
  const { data: baseEthBalance } = useBalance({
    address: ethAddress,
    chainId: getBaseChainId(),
  });

  // Fetch USDC balance on Base
  const { data: baseUsdcBalance } = useBalance({
    address: ethAddress,
    chainId: getBaseChainId(),
    token: BASE_USDC_CONTRACT,
  });

  // Fetch ARIO balance on Base
  const { data: baseArioBalance } = useBalance({
    address: ethAddress,
    chainId: getBaseChainId(),
    token: BASE_ARIO_CONTRACT,
  });

  // Format Base token balance for display
  const baseTokenBalance = useMemo(() => {
    if (!selectedBaseToken) return null;
    if (selectedBaseToken === 'base-eth' && baseEthBalance) {
      return {
        value: Number(baseEthBalance.formatted),
        formatted: `${Number(baseEthBalance.formatted).toFixed(6)} ETH`,
      };
    }
    if (selectedBaseToken === 'base-usdc' && baseUsdcBalance) {
      return {
        value: Number(baseUsdcBalance.formatted),
        formatted: `${Number(baseUsdcBalance.formatted).toFixed(2)} USDC`,
      };
    }
    if (selectedBaseToken === 'base-ario' && baseArioBalance) {
      return {
        value: Number(baseArioBalance.formatted),
        formatted: `${Number(baseArioBalance.formatted).toFixed(2)} ARIO`,
      };
    }
    return null;
  }, [selectedBaseToken, baseEthBalance, baseUsdcBalance, baseArioBalance]);

  const selectedCryptoBalance = useMemo(() => {
    if (selectedCrypto === formattedARIOTicker) {
      return allArIOBalance;
    }
    // For Base tokens, we don't show balance here (user will pay during transaction)
    return 0;
  }, [selectedCrypto, allArIOBalance, formattedARIOTicker]);

  const [showTopupModal, setShowTopupModal] = useState(false);

  // Check if selected token is a Base token
  const isBaseTokenSelected = isBaseToken(selectedCrypto);

  useEffect(() => {
    if (DISABLE_CREDIT_CARD_CHECKOUT_UI && paymentMethod === 'card') {
      onPaymentMethodChange('crypto');
    }
    if (DISABLE_TURBO_CREDITS_CHECKOUT_UI && paymentMethod === 'credits') {
      onPaymentMethodChange('crypto');
    }
  }, [paymentMethod, onPaymentMethodChange]);

  useEffect(() => {
    if (!DISABLE_BASE_CRYPTO_CHECKOUT_UI || !isBaseToken(selectedCrypto))
      return;
    setSelectedCrypto(formattedARIOTicker);
    onCryptoTokenChange?.('ario');
  }, [
    DISABLE_BASE_CRYPTO_CHECKOUT_UI,
    selectedCrypto,
    formattedARIOTicker,
    onCryptoTokenChange,
  ]);

  useEffect(() => {
    if (paymentMethod === 'credits') {
      setIsValid(!isInsufficientBalance);
    } else if (paymentMethod === 'crypto') {
      // For Base tokens, we're always valid (balance check happens at transaction time)
      // For ARIO, check the balance
      if (isBaseTokenSelected) {
        setIsValid(true);
      } else {
        setIsValid(!isInsufficientBalance);
      }
    }
  }, [paymentMethod, isInsufficientBalance, isBaseTokenSelected]);

  return (
    <>
      <div className="flex flex-col gap-6 w-full">
        {/* tabs */}
        <Tabs.Root
          className="w-full text-white flex flex-col h-full"
          value={paymentMethod}
          onValueChange={(value) =>
            onPaymentMethodChange(value as PaymentMethod)
          }
        >
          <Tabs.List
            defaultValue={'crypto'}
            className="flex w-full justify-center items-center gap-2 mb-6"
          >
            {/*
              Credit-card: Turbo payment service / AO settlement — not wired for
              Solana checkout yet. See `TurboArNSClient.ts`. Gated by
              DISABLE_CREDIT_CARD_CHECKOUT_UI with the rest of the non-crypto paths.
            */}
            <Tabs.Trigger
              value="card"
              disabled={DISABLE_CREDIT_CARD_CHECKOUT_UI}
              title="Credit-card payments for ArNS purchases are temporarily unavailable on Solana. The Turbo payment service needs Solana support before this option can be re-enabled. Use crypto in the meantime."
              className="flex gap-3 p-3 data-[state=active]:bg-foreground rounded border border-[#222224] data-[state=active]:border-grey text-white items-center flex-1 whitespace-nowrap transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex gap-3 items-center">
                <CreditCard className="size-5 text-grey" />
                Credit Card
              </div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="crypto"
              className="flex gap-3 p-3 data-[state=active]:bg-foreground rounded border border-[#222224] data-[state=active]:border-grey text-white items-center flex-1 whitespace-nowrap transition-all duration-300"
            >
              <ArIOTokenIcon className="size-5 text-grey fill-grey rounded-full border border-grey" />{' '}
              Crypto
            </Tabs.Trigger>
            <Tabs.Trigger
              value="credits"
              disabled={DISABLE_TURBO_CREDITS_CHECKOUT_UI}
              title="Turbo credits checkout is temporarily disabled for this build."
              className="flex gap-3 p-3 data-[state=active]:bg-foreground rounded border border-[#222224] data-[state=active]:border-grey text-white items-center flex-1 whitespace-nowrap transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex gap-3 items-center">
                <TurboIcon
                  className="size-5 text-grey"
                  stroke="var(--text-grey)"
                />{' '}
                Credits
              </div>
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content
            value="card"
            className={`flex flex-col data-[state=active]:p-6 data-[state=active]:border border-dark-grey rounded h-full data-[state=inactive]:size-0 data-[state=inactive]:opacity-0 data-[state=active]:min-h-[405px]`}
          >
            <CardPanel
              setIsValid={paymentMethod === 'card' ? setIsValid : () => null}
              onPaymentInformationChange={onPaymentInformationChange}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
            />
          </Tabs.Content>
          <Tabs.Content
            value="crypto"
            className={`flex flex-col data-[state=active]:p-6 data-[state=active]:border border-dark-grey rounded h-full data-[state=inactive]:size-0 data-[state=inactive]:opacity-0 data-[state=active]:min-h-[405px]`}
          >
            {' '}
            <SelectDropdown
              value={selectedCrypto}
              onChange={(value) => {
                const newValue = value as CryptoOptions;
                setSelectedCrypto(newValue);
                // Immediately notify parent to avoid delay from useEffect
                if (onCryptoTokenChange) {
                  const token: CryptoPaymentToken = isBaseToken(newValue)
                    ? (newValue as BaseTokenType)
                    : 'ario';
                  onCryptoTokenChange(token);
                }
              }}
              position="popper"
              side="bottom"
              renderValue={() => {
                // Show appropriate label for the selected option
                const selectedOption = cryptoDropdownOptions.find(
                  (opt) => opt.value === selectedCrypto,
                );
                return (
                  <span className="text-white flex w-full">
                    {selectedOption?.label || selectedCrypto}
                  </span>
                );
              }}
              className={{
                trigger: `flex w-full gap-2 p-3 rounded-md bg-transparent border border-[#222224] items-center justify-between ${
                  cryptoDropdownOptions.length > 1 ? '' : 'pointer-events-none'
                }`,
                icon:
                  cryptoDropdownOptions.length > 1
                    ? 'text-grey size-5'
                    : 'text-transparent size-5',
                content:
                  'bg-foreground z-[100] rounded overflow-hidden border py-2 border-dark-grey w-[--radix-select-trigger-width]',
                item: 'flex items-center gap-3 cursor-pointer bg-foreground hover:bg-dark-grey px-3 py-3 text-grey hover:text-white outline-none transition-all',
                group: 'flex flex-col bg-foreground text-sm',
                viewport: 'flex flex-col',
              }}
              options={cryptoDropdownOptions}
            />
            {/* Base Token Payment Info */}
            {isBaseTokenSelected && selectedBaseToken && (
              <div className="flex size-full mt-4 flex-col gap-4">
                {/* Balance Display */}
                <div className="flex flex-col gap-2 items-start">
                  <span className="text-grey text-xs">
                    Your {BASE_TOKEN_CONFIG[selectedBaseToken].symbol} balance
                    on Base:
                  </span>
                  <span className="text-white text-2xl font-bold">
                    {baseTokenBalance ? (
                      baseTokenBalance.formatted
                    ) : ethAddress ? (
                      <span className="text-grey animate-pulse">
                        Loading...
                      </span>
                    ) : (
                      <span className="text-grey">--</span>
                    )}
                  </span>
                </div>

                <div className="flex items-start gap-2 p-3 rounded bg-foreground border border-dark-grey">
                  <Info className="size-4 text-grey flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1 text-xs text-grey">
                    <span>
                      You&apos;ll pay with{' '}
                      {BASE_TOKEN_CONFIG[selectedBaseToken].symbol} on the{' '}
                      {BASE_TOKEN_CONFIG[selectedBaseToken].networkName}{' '}
                      network.
                    </span>
                    <span>
                      Your wallet will prompt you to switch networks and approve
                      the transaction.
                    </span>
                    <span className="text-white/60">
                      A 2% buffer is included to account for price fluctuations.
                    </span>
                  </div>
                </div>
              </div>
            )}
            {/* ARIO Balance and Options */}
            {!isBaseTokenSelected && (
              <>
                <div className="flex size-full mt-4">
                  <div className="flex flex-col gap-2 items-start">
                    <span className="text-grey text-xs">
                      Your total {selectedCrypto} balance:
                    </span>
                    <span className="text-white text-2xl font-bold">
                      {formatARIOWithCommas(selectedCryptoBalance)}{' '}
                      {selectedCrypto}
                    </span>
                  </div>
                </div>
                {selectedCrypto === formattedARIOTicker && (
                  <div className="flex flex-col gap-2 size-full items-start">
                    <span className="text-grey text-xs">
                      Select balance method:
                    </span>
                    <ARIOFundingSelector
                      fundingSource={fundingSource}
                      onChange={onFundingSourceChange}
                    />
                  </div>
                )}
              </>
            )}
          </Tabs.Content>
          <Tabs.Content
            value="credits"
            className={`flex flex-col data-[state=active]:p-6 data-[state=active]:border border-dark-grey rounded h-full data-[state=inactive]:size-0 data-[state=inactive]:opacity-0 data-[state=active]:min-h-[405px]`}
          >
            <div className="flex w-full flex-col gap-2 items-start">
              <span className="text-grey text-sm">
                Your Turbo Credit balance:
              </span>
              <span className="text-white text-2xl font-bold">
                {turboCreditBalance} Credits
              </span>
            </div>{' '}
            {isInsufficientBalance && (
              <div className="flex size-full flex-col items-start justify-between">
                {' '}
                <div className="flex flex-col text-xs text-error items-start mt-6">
                  <span>Insufficient Turbo Credits for purchase.</span>
                  <span>
                    Please top-up to complete your purchase in credits.
                  </span>
                </div>
                <button
                  type="button"
                  disabled={DISABLE_TURBO_CREDITS_CHECKOUT_UI}
                  className="py-2 px-6 text-lg text-white rounded bg-dark-grey border border-transparent hover:border-grey disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => setShowTopupModal(true)}
                >
                  Top-Up
                </button>
              </div>
            )}
          </Tabs.Content>
        </Tabs.Root>{' '}
      </div>
      {showTopupModal && (
        <TurboTopUpModal onClose={() => setShowTopupModal(false)} />
      )}
    </>
  );
}

export default PaymentOptionsForm;
